import { Button } from "@heroui/react";
import useCalendarStore from "@/store/calendar-store";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { isSameWeek, startOfWeek, subWeeks } from "date-fns";
import { useLocale, useTranslations } from "../../../../../i18n";
import { capitalize } from "@/utils/capitalize";
import {
  dateToLocalTime,
  formatDateWithLocale,
} from "@/utils/format-date-with-locale";
import UserMenu from "@/components/user-menu";
import { useStudioStore } from "@/store/studio-store";
import { useBreakpoint } from "@/hooks/use-media-query";

const CalendarHeader = ({
  selectedView,
  isAdmin,
}: {
  selectedView?: string;
  isAdmin?: boolean;
  view?: string;
}) => {
  const studio = useStudioStore((state) => state.studio);
  const timezone = studio?.timezone || "Europe/Zurich";
  const locale = useLocale();
  const { isMobile } = useBreakpoint();

  const t = useTranslations("calendar");
  const { view, currentDate, setCurrentDate, setStartOfTheWeek } =
    useCalendarStore();

  const handlePrevious = () => {
    const newDate = new Date(currentDate);
    let newStartOfTheWeek;

    if (view === "day") {
      // Convert to organization timezone before calculations
      const localDate = dateToLocalTime(currentDate, timezone);
      // For day view, go back to the previous week's Monday
      newStartOfTheWeek = subWeeks(
        startOfWeek(localDate, { weekStartsOn: 1 }),
        1
      );
      setCurrentDate(newStartOfTheWeek); // Set date to the previous Monday
    } else if (view === "week" || view === "agenda") {
      // For week view, go back one week
      newDate.setDate(newDate.getDate() - 7);
      newStartOfTheWeek = startOfWeek(dateToLocalTime(newDate, timezone), {
        weekStartsOn: 1,
      });
      setCurrentDate(newDate);
    }

    setStartOfTheWeek(newStartOfTheWeek);
  };

  const handleNext = () => {
    const newDate = new Date(currentDate);
    let newStartOfTheWeek;

    if (view === "day") {
      // Convert current date to organization timezone
      const localDate = dateToLocalTime(new Date(currentDate), timezone);

      // For day view, go forward to the next Monday
      // First, get the current day of week (0-6, where 0 is Sunday)
      const dayOfWeek = localDate.getDay();

      // Calculate days to add to get to next Monday
      // If today is Monday (1), add 7 days
      // If today is Tuesday (2), add 6 days
      // ...
      // If today is Sunday (0), add 1 day
      const daysToAdd = dayOfWeek === 0 ? 1 : 8 - dayOfWeek;

      // Create new date by adding the calculated days
      newDate.setDate(newDate.getDate() + daysToAdd);

      // Calculate the start of the week for context
      newStartOfTheWeek = startOfWeek(dateToLocalTime(newDate, timezone), {
        weekStartsOn: 1,
      });

      // Set the current date to the next Monday
      setCurrentDate(newDate);
    } else if (view === "week" || view === "agenda") {
      // For week view, go forward one week
      newDate.setDate(newDate.getDate() + 7);
      newStartOfTheWeek = startOfWeek(dateToLocalTime(newDate, timezone), {
        weekStartsOn: 1,
      });
      setCurrentDate(newDate);
    } else if (view === "month") {
      // For month view, go forward one month
      newDate.setMonth(newDate.getMonth() + 1);
      newStartOfTheWeek = startOfWeek(dateToLocalTime(newDate, timezone), {
        weekStartsOn: 1,
      });
      setCurrentDate(newDate);
    }

    setStartOfTheWeek(newStartOfTheWeek);
  };

  const isCurrentWeek = () => {
    if (isAdmin) return false;

    const today = new Date();
    const localToday = dateToLocalTime(today, timezone);
    const localCurrentDate = dateToLocalTime(currentDate, timezone);

    return isSameWeek(localCurrentDate, localToday, { weekStartsOn: 1 });
  };

  return (
    <div
      className={`w-full mx-auto flex flex-col md:flex-row md:justify-between items-end md:items-center gap-4 py-4 ${view === "agenda" ? "max-w-5xl" : "max-w-7xl"}`}
    >
      {isMobile && <UserMenu />}
      <div
        className="flex items-center gap-4"
        style={{
          justifyContent:
            selectedView === "admin" ? "w-full space-between" : "flex-start",
        }}
      >
        <h2 className="text-xl font-semibold">
          {capitalize(
            formatDateWithLocale(currentDate, "MMMM yyyy", locale, timezone)
          )}
        </h2>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            color="primary"
            onPress={handlePrevious}
            isIconOnly
            isDisabled={isCurrentWeek()}
            aria-label={t("buttons.previous")}
          >
            <IconChevronLeft size={24} />
          </Button>
          <Button variant="ghost" color="primary" onPress={handleNext}>
            <IconChevronRight size={24} />
            <span>
              {view === "week" || view === "day" || view === "agenda"
                ? t("buttons.nextWeek")
                : t("buttons.nextMonth")}
            </span>
          </Button>
        </div>
      </div>

      {!isMobile && <UserMenu />}
    </div>
  );
};

export default CalendarHeader;
